import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import { useAdminPanelTheme } from '../../project-modules/store/AdminPanelThemeContext';
import { fetchBillingAccountSummary } from '../../api/billing';
import SuperAdminBalanceChip from './SuperAdminBalanceChip';
import { isCatalogOwnerBillingRole } from './billingUiVisibility';

const SuperAdminAccountDrawer = lazy(() => import('./SuperAdminAccountDrawer'));

export default function SuperAdminTopBar({
  title,
  subtitle,
  role,
  username,
  showCabinetLink = false,
  projectSlug = '',
  projectLoginLink = '',
  onCopyLoginLink,
  storeReturnTo = '',
  onLogout,
  children,
  billingSummary: billingSummaryProp,
  billingLoading: billingLoadingProp,
  fetchBillingInternally = true,
}) {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [internalBilling, setInternalBilling] = useState(null);
  const [internalBillingLoading, setInternalBillingLoading] = useState(false);
  const { sa } = usePublicUiLocale();
  const { ui } = useAdminPanelTheme();
  const initial = (username || role || '?').charAt(0).toUpperCase();

  const showBilling = isCatalogOwnerBillingRole(role);
  const billingManagedExternally = billingSummaryProp !== undefined;
  const billingSummary = showBilling
    ? (billingManagedExternally ? billingSummaryProp : internalBilling)
    : null;
  const billingLoading = showBilling
    ? (billingManagedExternally ? Boolean(billingLoadingProp) : internalBillingLoading)
    : false;

  const loadBilling = useCallback(async () => {
    if (!showBilling) return;
    setInternalBillingLoading(true);
    try {
      const data = await fetchBillingAccountSummary();
      setInternalBilling(data);
    } catch {
      setInternalBilling(null);
    } finally {
      setInternalBillingLoading(false);
    }
  }, [showBilling]);

  useEffect(() => {
    if (!showBilling || !fetchBillingInternally || billingManagedExternally) {
      return undefined;
    }
    loadBilling();
    return undefined;
  }, [showBilling, fetchBillingInternally, billingManagedExternally, loadBilling]);

  useEffect(() => {
    if (!showBilling || !drawerOpen || billingManagedExternally) return undefined;
    loadBilling();
    return undefined;
  }, [showBilling, drawerOpen, billingManagedExternally, loadBilling]);

  const actions = (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ flex: '0 0 auto', justifyContent: 'flex-end' }}
    >
      {children}
      {showBilling ? (
        <SuperAdminBalanceChip
          summary={billingSummary}
          loading={billingLoading}
          onClick={() => setDrawerOpen(true)}
        />
      ) : null}
      {projectLoginLink && onCopyLoginLink ? (
        <Tooltip title={sa.loginLinkTooltip}>
          <IconButton
            size="small"
            onClick={onCopyLoginLink}
            sx={{ color: ui.textSecondary, flexShrink: 0 }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title={sa.accountMenuTooltip}>
        <IconButton
          data-help="account-button"
          onClick={() => setDrawerOpen(true)}
          sx={{ p: 0.5, flexShrink: 0 }}
          aria-label={sa.accountMenuTooltip}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: role === 'superadmin' ? '#ed6c02' : '#0288d1',
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            {initial}
          </Avatar>
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const titleBlock = (
    <Box sx={{ minWidth: 0, textAlign: showCabinetLink ? 'center' : 'left' }}>
      <Typography variant="h6" noWrap sx={{ color: ui.textPrimary, lineHeight: 1.25 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" noWrap sx={{ color: ui.textSecondary, display: 'block', lineHeight: 1.3 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <>
      {showCabinetLink ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ justifySelf: 'start', minWidth: 0 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/v2/superadmin')}
              sx={{
                ml: -0.5,
                color: ui.textSecondary,
                maxWidth: '100%',
              }}
            >
              {sa.cabinetLinkPrimary}
            </Button>
          </Box>
          <Box sx={{ minWidth: 0, maxWidth: '100%', px: 1 }}>
            {titleBlock}
          </Box>
          <Box sx={{ justifySelf: 'end', minWidth: 0 }}>
            {actions}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
            flexWrap: 'nowrap',
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            {titleBlock}
          </Box>
          {actions}
        </Box>
      )}

      {drawerOpen ? (
        <Suspense fallback={null}>
          <SuperAdminAccountDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            username={username}
            role={role}
            projectSlug={projectSlug}
            projectLoginLink={projectLoginLink}
            onCopyLoginLink={onCopyLoginLink}
            storeReturnTo={storeReturnTo}
            billingSummary={showBilling ? billingSummary : null}
            billingLoading={showBilling ? billingLoading : false}
            onLogout={() => {
              setDrawerOpen(false);
              onLogout();
            }}
          />
        </Suspense>
      ) : null}
    </>
  );
}

SuperAdminTopBar.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  role: PropTypes.string,
  username: PropTypes.string,
  showCabinetLink: PropTypes.bool,
  projectSlug: PropTypes.string,
  projectLoginLink: PropTypes.string,
  onCopyLoginLink: PropTypes.func,
  storeReturnTo: PropTypes.string,
  onLogout: PropTypes.func.isRequired,
  children: PropTypes.node,
  billingSummary: PropTypes.object,
  billingLoading: PropTypes.bool,
  fetchBillingInternally: PropTypes.bool,
};
